import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Gauge } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LEVEL_CLS } from "./useAcademy";

const LEVELS = [{ key: "aware", label: "Aware" }, { key: "working", label: "Working" }, { key: "proficient", label: "Proficient" }, { key: "expert", label: "Expert" }];

function Row({ comp, value, onChange }) {
  return (
    <div className="rounded-xl border border-slate-200/80 p-3.5" data-testid={`sa-row-${comp.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-slate-900 text-sm">{comp.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Required at your stage: <span className={cn("px-1.5 py-0.5 rounded border", LEVEL_CLS[comp.required_level])}>{comp.required_level_label}</span></div>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" role="radiogroup" aria-label={`${comp.name} self-rating`}>
          {LEVELS.map((l) => (
            <button key={l.key} type="button" role="radio" aria-checked={value.level === l.key} onClick={() => onChange({ ...value, level: l.key })}
              data-testid={`sa-level-${comp.key}-${l.key}`}
              className={cn("px-2.5 py-1 rounded-md text-xs transition-colors", value.level === l.key ? "bg-emerald-600 text-white shadow-sm" : "text-slate-600 hover:bg-white")}>{l.label}</button>
          ))}
        </div>
      </div>
      <Input value={value.note || ""} onChange={(e) => onChange({ ...value, note: e.target.value })} placeholder="Optional note — e.g. strong on Scope 1-2, no Scope 3 yet" className="h-8 mt-2.5 text-xs" data-testid={`sa-note-${comp.key}`} />
    </div>
  );
}

export function SelfAssessmentDialog({ open, onClose, journey }) {
  const qc = useQueryClient();
  const [ratings, setRatings] = useState({});
  useEffect(() => {
    if (open) setRatings(Object.fromEntries(journey.competencies.map((c) => [c.key, { level: c.self_level || null, note: c.self_note || "" }])));
  }, [open, journey]);
  const rated = Object.values(ratings).filter((r) => r.level).length;
  const save = useMutation({
    mutationFn: () => api.put("/academy/self-assessment", { ratings: Object.fromEntries(Object.entries(ratings).filter(([, r]) => r.level)) }),
    onSuccess: () => { ["journey", "digest", "nba"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success("Self-assessment saved — your gaps are now personalised"); onClose(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="self-assessment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-emerald-600" /> Rate yourself</DialogTitle>
          <DialogDescription>Where are you honestly today in each domain? Your gap = required level at your stage − your rating. This is private to you and only shapes recommendations.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {journey.competencies.map((c) => <Row key={c.key} comp={c} value={ratings[c.key] || { level: null, note: "" }} onChange={(v) => setRatings((r) => ({ ...r, [c.key]: v }))} />)}
        </div>
        <DialogFooter className="gap-2">
          <span className="text-xs text-slate-500 self-center mr-auto" data-testid="sa-rated-count">{rated}/{journey.competencies.length} rated</span>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || rated === 0} data-testid="sa-save" className="bg-emerald-600 hover:bg-emerald-700">{save.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save self-assessment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
